import { useState } from 'react';
import { Box, Divider, Flex, Menu, Modal, Stack, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { Button } from 'src/design-system/Button';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { useCalendarEventMeetingAgenda } from 'src/meetings/hooks/useCalendarEventMeetingAgenda';
import { useGenerateCalendarEventMeetingNote } from 'src/meetings/hooks/useGenerateCalendarEventMeetingNote';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';
import { showError, showSuccess } from 'src/notifications/notifications';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AttendeeRow } from './AttendeeRow';
import { type Attendee, useMeetingAttendees } from './useMeetingAttendees';

type Props = {
  opened: boolean;
  onClose: () => void;
  scope: MeetingScope;
  meeting: FamilyMeeting;
};

type NoteSource = 'agenda' | 'template';

function resolveSource(source: NoteSource, hasAgenda: boolean, hasTemplate: boolean): NoteSource {
  if (source === 'agenda' && !hasAgenda && hasTemplate) return 'template';
  if (source === 'template' && !hasTemplate && hasAgenda) return 'agenda';
  return source;
}

export function GenerateMeetingNoteModal({ opened, onClose, scope, meeting }: Props) {
  const attendees = useMeetingAttendees(scope, meeting);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [source, setSource] = useState<NoteSource>('agenda');

  const { generateMeetingNote, isLoading: isGenerating } = useGenerateCalendarEventMeetingNote(
    meeting.id,
  );
  const { files: templates } = useTemplates({ templateType: 'MEETING_NOTE' });
  const defaultTemplate = templates.find(template => template.isDefault) ?? templates[0];
  const { meetingAgenda } = useCalendarEventMeetingAgenda({
    eventId: meeting.id,
    enabled: !!meeting.hasMeetingAgenda,
  });

  const isChecked = (attendee: Attendee) => overrides[attendee.id] ?? attendee.defaultChecked;
  const toggle = (attendee: Attendee) =>
    setOverrides(previous => ({ ...previous, [attendee.id]: !isChecked(attendee) }));

  const hasAgenda = meetingAgenda?.fileId != null;
  const hasTemplate = defaultTemplate != null;

  // Fall back to the available source if the selected one isn't usable.
  const effectiveSource = resolveSource(source, hasAgenda, hasTemplate);

  const canGenerate =
    (effectiveSource === 'agenda' && hasAgenda) || (effectiveSource === 'template' && hasTemplate);

  const sourceTitle = effectiveSource === 'agenda' ? 'Create from agenda' : 'Create from template';
  const sourceSubtext =
    effectiveSource === 'agenda' ? 'Match agenda format' : 'Use a meeting note template';

  const handleGenerate = async () => {
    try {
      if (effectiveSource === 'agenda' && meetingAgenda?.fileId != null) {
        await generateMeetingNote({ agendaFileId: String(meetingAgenda.fileId), summarise: true });
      } else if (effectiveSource === 'template' && defaultTemplate) {
        await generateMeetingNote({ templateId: defaultTemplate.id, summarise: true });
      } else {
        showError('No meeting agenda or note template is available to generate from.');
        return;
      }
      showSuccess('Generating meeting note');
      onClose();
    } catch (error) {
      notifyAndLogError(
        'Failed to generate meeting note',
        'Could not generate the meeting note. Please try again.',
        error,
      );
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Generate meeting note" size={440}>
      <Stack gap="xxxs">
        {attendees.map(attendee => (
          <AttendeeRow
            key={attendee.id}
            name={attendee.name}
            role={attendee.role}
            checked={isChecked(attendee)}
            onToggle={() => toggle(attendee)}
            ariaLabel={`${attendee.name} attended`}
          />
        ))}
      </Stack>

      <Divider color="gray.1" mt="md" />

      <Text mt="md" size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
        Choose how to generate the meeting note
      </Text>

      <Menu position="bottom-start" width="target">
        <Menu.Target>
          <Flex
            mt="xxs"
            justify="space-between"
            align="center"
            gap="sm"
            py="xs"
            px="sm"
            style={{
              border: '1px solid var(--mantine-color-gray-1)',
              borderRadius: 16,
              cursor: 'pointer',
            }}
          >
            <Box>
              <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                {sourceTitle}
              </Text>
              <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6" mt={4}>
                {sourceSubtext}
              </Text>
            </Box>
            <IconChevronDown
              size={24}
              color="var(--mantine-color-gray-10)"
              style={{ flexShrink: 0 }}
            />
          </Flex>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item disabled={!hasAgenda} onClick={() => setSource('agenda')}>
            Create from agenda
          </Menu.Item>
          <Menu.Item disabled={!hasTemplate} onClick={() => setSource('template')}>
            Create from template
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <Box mt="lg">
        <Button
          fullWidth
          onClick={handleGenerate}
          loading={isGenerating}
          disabled={!canGenerate || isGenerating}
        >
          Generate meeting note
        </Button>
      </Box>
    </Modal>
  );
}
